import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { VOICE_BITS_PER_SECOND, VOICE_MAX_SECONDS } from "./limits";
import { pickRecorderMime } from "./mime";
import { initialRecorderState, recorderReducer, shouldAutoStop } from "./recorderMachine";

export interface RecordedClip {
  blob: Blob;
  url: string;
  mimeType: string;
  durationSeconds: number;
}

/**
 * MediaRecorder wrapper (DD-046). Records, stops at maxSeconds, and on an
 * interruption (tab hidden, iOS screen lock, track ended) keeps what it has.
 * The reducer ignores start while a clip is recorded: call reset() first to
 * re-record.
 */
export function useVoiceRecorder(maxSeconds: number = VOICE_MAX_SECONDS) {
  const [state, dispatch] = useReducer(recorderReducer, initialRecorderState);
  const [clip, setClip] = useState<RecordedClip | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const clipUrlRef = useRef<string | null>(null);
  /** Set once unmounted, so a late permission grant can't leave the mic on. */
  const disposedRef = useRef(false);

  const releaseStream = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") recorder.stop();
  }, []);

  const start = useCallback(async () => {
    const capable =
      window.isSecureContext &&
      typeof MediaRecorder !== "undefined" &&
      typeof navigator.mediaDevices?.getUserMedia === "function";
    const mimeType = capable ? pickRecorderMime((mime) => MediaRecorder.isTypeSupported(mime)) : null;
    if (!mimeType) {
      dispatch({ type: "unsupported" });
      return;
    }
    dispatch({ type: "request" });
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      if (!disposedRef.current) dispatch({ type: "denied" });
      return;
    }
    // Closed while the permission prompt was open: turn the mic straight off.
    if (disposedRef.current) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    streamRef.current = stream;
    chunksRef.current = [];
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: VOICE_BITS_PER_SECOND });
    } catch {
      releaseStream();
      dispatch({ type: "unsupported" });
      return;
    }
    recorderRef.current = recorder;
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      const type = recorder.mimeType || mimeType;
      const blob = new Blob(chunksRef.current, { type });
      // The auto-stop lands a moment past the ceiling, and the backend
      // answers a duration above it with 413.
      const elapsed = Math.round((Date.now() - startedAtRef.current) / 1000);
      const durationSeconds = Math.min(maxSeconds, Math.max(1, elapsed));
      releaseStream();
      const url = URL.createObjectURL(blob);
      clipUrlRef.current = url;
      setClip({ blob, url, mimeType: type, durationSeconds });
      dispatch({ type: "stopped" });
    };
    stream.getAudioTracks().forEach((track) => {
      track.onended = () => stop();
    });
    startedAtRef.current = Date.now();
    try {
      recorder.start(1000);
    } catch {
      recorder.onstop = null;
      recorderRef.current = null;
      releaseStream();
      dispatch({ type: "unsupported" });
      return;
    }
    dispatch({ type: "granted" });
    timerRef.current = window.setInterval(() => {
      dispatch({ type: "tick", elapsedSeconds: Math.floor((Date.now() - startedAtRef.current) / 1000) });
    }, 250);
  }, [maxSeconds, releaseStream, stop]);

  const reset = useCallback(() => {
    if (clipUrlRef.current) URL.revokeObjectURL(clipUrlRef.current);
    clipUrlRef.current = null;
    setClip(null);
    dispatch({ type: "reset" });
  }, []);

  useEffect(() => {
    if (shouldAutoStop(state, maxSeconds)) stop();
  }, [state, maxSeconds, stop]);

  const isRecording = state.status === "recording";
  useEffect(() => {
    if (!isRecording) return undefined;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") stop();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [isRecording, stop]);

  useEffect(() => {
    // Reset on mount too: StrictMode runs this cleanup once before the real mount.
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
      const recorder = recorderRef.current;
      if (recorder) {
        recorder.onstop = null;
        if (recorder.state !== "inactive") recorder.stop();
      }
      releaseStream();
      if (clipUrlRef.current) URL.revokeObjectURL(clipUrlRef.current);
    };
  }, [releaseStream]);

  return { state, clip, start, stop, reset };
}
